import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/** Payment outcome values the backend returns on the Stripe return URL. */
const PAYMENT_STATUS_VALUES = ["payment-succeeded", "payment-failed"];

/**
 * Global handler for Stripe Checkout returns.
 *
 * When a Stripe payment completes, the backend redirects the user back to the
 * `return_path` supplied at checkout with a payment-status query param
 * (e.g. `?payment_status=payment-succeeded&session_id=...`). Because Buy Credits
 * can be launched from any page, this hook is mounted once at the app root so the
 * existing toast fires on whatever page the user lands on. It then strips the
 * payment params from the URL so a refresh/back-nav won't re-toast.
 *
 * IMPORTANT: the payment outcome rides its own `payment_status` param — NOT the
 * generic `status` param. Several pages (My Candidates, My Jobs, …) use `?status=`
 * for their own KPI/filter state; reading/deleting that here stripped their filter
 * on every render (a selected KPI card reverted to "All"). This hook now only
 * touches `payment_status`, leaving page-level `status` untouched.
 *
 * Uses the existing toast copy verbatim — no new/modified messaging.
 */
export const usePaymentReturnStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Prefer the dedicated `payment_status` param. Fall back to a legacy `status`
    // ONLY when it holds a payment value, so during the backend rename rollout an
    // old `?status=payment-succeeded` still toasts — while a normal page filter
    // (e.g. `?status=qualified`) is never treated as payment and never stripped.
    const paymentStatus = params.get("payment_status");
    const legacyStatus = params.get("status");
    const carrierKey =
      paymentStatus && PAYMENT_STATUS_VALUES.includes(paymentStatus)
        ? "payment_status"
        : legacyStatus && PAYMENT_STATUS_VALUES.includes(legacyStatus)
          ? "status"
          : null;
    if (!carrierKey) return;

    const value = carrierKey === "payment_status" ? paymentStatus : legacyStatus;

    if (value === "payment-succeeded") {
      toast.success(
        "Payment successful! Credits have been added to your account.",
      );
    } else if (value === "payment-failed") {
      toast.info("Payment cancelled.");
    }

    // Strip only the param that actually carried the payment outcome (+ session_id)
    // so a refresh/back-nav won't re-toast, without disturbing any other param.
    params.delete(carrierKey);
    params.delete("session_id");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }, [location.search, location.pathname, navigate]);
};

export default usePaymentReturnStatus;
